import dayjs from 'dayjs';

import {
  WorkloadGpuUtilizationSummary,
  WorkloadGpuUtilizationSummaryInterval,
} from '@cast/types';
import { toBig } from '@cast/utils';

import {
  NormalizedWorkloadGpuUtilizationSummary,
  NormalizedWorkloadGpuUtilizationSummaryInterval,
} from '../_types';

const normalizeIntervals = (
  intervals: WorkloadGpuUtilizationSummaryInterval[]
): NormalizedWorkloadGpuUtilizationSummaryInterval[] => {
  if (!intervals) {
    return [];
  }

  return intervals.map((item) => {
    return {
      timestamp: dayjs.utc(item.timestamp).subtract(1, 'day').toISOString(),
      idleMinutes: toBig(item.idleMinutes || 0).toNumber(),
      uptimeMinutes: toBig(item.uptimeMinutes || 0).toNumber(),
      podCount: toBig(item.podCount || 0).toNumber(),
      utilization: item.utilization
        ? {
            gpuPercent: toBig(item.utilization.gpuPercent || 0).toNumber(),
            memoryPercent: toBig(
              item.utilization.memoryPercent || 0
            ).toNumber(),
          }
        : undefined,
      memory: item.memory
        ? {
            availableBytes: toBig(item.memory.availableBytes || 0).toNumber(),
            usedBytes: toBig(item.memory.usedBytes || 0).toNumber(),
            wastedBytes: toBig(item.memory.wastedBytes || 0).toNumber(),
          }
        : undefined,
      costImpact: item.costImpact
        ? {
            onDemand: toBig(item.costImpact.onDemand || 0).toNumber(),
            spot: toBig(item.costImpact.spot || 0).toNumber(),
            spotFallback: toBig(item.costImpact.spotFallback || 0).toNumber(),
            total: toBig(item.costImpact.onDemand || 0)
              .add(toBig(item.costImpact.spot || 0))
              .add(toBig(item.costImpact.spotFallback || 0))
              .toNumber(),
          }
        : undefined,
    };
  });
};

export const normalizeWorkloadGpuUtilization = (
  data: WorkloadGpuUtilizationSummary
): NormalizedWorkloadGpuUtilizationSummary => {
  return {
    ...data,
    avgGpuUtilization: toBig(data.avgGpuUtilization || 0).toNumber(),
    avgGpuMemoryUtilization: toBig(
      data.avgGpuMemoryUtilization || 0
    ).toNumber(),
    totalGpuMemoryBytes: toBig(data.totalGpuMemoryBytes || 0).toNumber(),
    totalUsedGpuMemoryBytes: toBig(
      data.totalUsedGpuMemoryBytes || 0
    ).toNumber(),
    totalWastedGpuMemoryBytes: toBig(
      data.totalWastedGpuMemoryBytes || 0
    ).toNumber(),
    totalIdleMinutes: toBig(data.totalIdleMinutes || 0).toNumber(),
    totalUptimeMinutes: toBig(data.totalUptimeMinutes || 0).toNumber(),
    totalCostImpact: data.totalCostImpact
      ? {
          onDemand: toBig(data.totalCostImpact.onDemand || 0).toNumber(),
          spot: toBig(data.totalCostImpact.spot || 0).toNumber(),
          spotFallback: toBig(
            data.totalCostImpact.spotFallback || 0
          ).toNumber(),
          total: toBig(data.totalCostImpact.onDemand || 0)
            .add(toBig(data.totalCostImpact.spot || 0))
            .add(toBig(data.totalCostImpact.spotFallback || 0))
            .toNumber(),
        }
      : undefined,
    intervals: normalizeIntervals(data.intervals ?? []),
  };
};
